import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import ReconstructionMeasurementWorkspace from './ReconstructionMeasurementWorkspace';
import { reconstructionImage, pngBlob } from './reconstructionImage';
import { UNITS, distance, polygonInfo, measurementValue, validSavedState } from './measurementGeometry';

const EMPTY = { unit: 'm', reference: null, measurements: [] };
const button = 'btn btn-secondary radius';
const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 };
const format = value => value.toLocaleString(undefined, { maximumFractionDigits: 3 });
const cameraObject = object => {
  for (let node = object; node; node = node.parent) {
    if (node.userData.cs2n_role === 'camera' || node.name.startsWith('cs2n_camera_')) return true;
  }
  return false;
};
const dispose = object => {
  const geometries = new Set(), materials = new Set(), textures = new Set();
  object.traverse(node => {
    if (node.geometry) geometries.add(node.geometry);
    (Array.isArray(node.material) ? node.material : [node.material]).filter(Boolean).forEach(material => {
      materials.add(material);
      Object.values(material).forEach(value => { if (value && value.isTexture) textures.add(value); });
    });
  });
  geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()); textures.forEach(t => t.dispose());
};

export default function ReconstructionViewer({ url, storageKey, onCreateAnother }) {
  const host = useRef(null), engine = useRef(null), labelLayer = useRef(null);
  const [measurementOpen, setMeasurementOpen] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [saved, setSaved] = useState(EMPTY);
  const [restored, setRestored] = useState(false);
  const [mode, setMode] = useState('navigate');
  const [draft, setDraft] = useState([]);
  const [pendingReference, setPendingReference] = useState(null);
  const [referenceValue, setReferenceValue] = useState('');
  const [error, setError] = useState(null), [storageError, setStorageError] = useState(null);
  const [loading, setLoading] = useState(true), [progress, setProgress] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [options, setOptions] = useState({ cameras: true, mesh: true, black: false, white: false });
  const [capabilities, setCapabilities] = useState({ cameras: false, colors: false, mesh: false });
  const live = useRef({});
  const scale = saved.reference ? saved.reference.metres / distance(...saved.reference.points) : 1;
  live.current = { mode, draft, options, scale, unit: saved.unit, pitch: pitch * Math.PI / 180, count: saved.measurements.length };

  useEffect(() => {
    setRestored(false);
    let value = EMPTY;
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (validSavedState(parsed)) value = parsed;
    } catch (_) { setStorageError('Measurements cannot be saved in this browser.'); }
    setSaved(value);
    setReferenceValue(value.reference ? String(value.reference.metres / UNITS[value.unit]) : '');
    setRestored(storageKey);
  }, [storageKey]);

  useEffect(() => {
    if (restored !== storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(saved)); }
    catch (_) { setStorageError('Measurements cannot be saved in this browser.'); }
  }, [saved, restored, storageKey]);

  useEffect(() => {
    let cancelled = false, frame, observer, renderer, controls;
    const container = host.current;
    setLoading(true); setModelLoaded(false); setProgress(0); setError(null);
    const scene = new THREE.Scene(); scene.add(new THREE.AmbientLight('#ffffff', 3)); scene.background = new THREE.Color('#f5f6f8');
    const root = new THREE.Group(), overlay = new THREE.Group();
    scene.add(root); root.add(overlay);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.001, 100000);
    const raycaster = new THREE.Raycaster();
    const state = { root, overlay, scene, camera, records: [], pickables: [], labels: [], radius: 1, renderer: null, modelLoaded: false };
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      container.appendChild(renderer.domElement);
      renderer.domElement.style.touchAction = 'none';
      renderer.domElement.setAttribute('aria-label', '3D reconstruction. Use the measurement controls, then select points on the model.');
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      state.renderer = renderer; state.controls = controls;
      engine.current = state;
    } catch (_) {
      setError('The 3D viewer needs WebGL. You can still download the GLB below.'); setLoading(false);
      return undefined;
    }
    const resize = () => {
      const width = Math.max(container.clientWidth, 1), height = Math.max(container.clientHeight, 1);
      renderer.setSize(width, height); camera.aspect = width / height; camera.updateProjectionMatrix();
    };
    observer = new ResizeObserver(resize); observer.observe(container); resize();
    const animate = () => {
      if (cancelled) return;
      frame = requestAnimationFrame(animate); controls.update(); root.updateMatrixWorld(true);
      state.labels.forEach(({ element, position }) => {
        const p = root.localToWorld(position.clone()).project(camera);
        element.style.display = p.z < -1 || p.z > 1 ? 'none' : 'block';
        element.style.left = `${(p.x + 1) * container.clientWidth / 2}px`;
        element.style.top = `${(1 - p.y) * container.clientHeight / 2}px`;
      });
      renderer.render(scene, camera);
    };
    animate();
    new GLTFLoader().load(url, gltf => {
      if (cancelled) { dispose(gltf.scene); return; }
      root.add(gltf.scene);
      let hasCameras = false, hasColors = false, hasMesh = false;
      gltf.scene.traverse(object => {
        if (!object.isMesh && !object.isPoints && !object.isLine) return;
        const isCamera = cameraObject(object);
        if (isCamera) hasCameras = true;
        const geometry = object.geometry;
        // Unlit vertex colours match the source imagery without model-viewer
        // lighting exaggerating seams or darkening the reconstruction.
        if (!isCamera && (object.isMesh || object.isPoints)) {
          hasColors = hasColors || !!geometry.attributes.color;
          hasMesh = hasMesh || object.isMesh;
          if (object.isMesh) {
            const originals = Array.isArray(object.material) ? object.material : [object.material];
            const replacements = originals.map(m => new THREE.MeshBasicMaterial({
              map: m.map, color: m.color || '#ffffff', opacity: m.opacity, transparent: m.transparent, alphaTest: m.alphaTest,
              vertexColors: !!geometry.attributes.color, side: THREE.DoubleSide
            }));
            originals.forEach(m => m.dispose());
            object.material = Array.isArray(object.material) ? replacements : replacements[0];
          }
        }
        state.records.push({ object, geometry, isCamera, filtered: null, points: null });
      });
      const bounds = new THREE.Box3();
      state.records.filter(r => !r.isCamera).forEach(r => bounds.expandByObject(r.object));
      if (bounds.isEmpty()) bounds.setFromObject(gltf.scene);
      const center = bounds.getCenter(new THREE.Vector3());
      const radius = Math.max(bounds.getSize(new THREE.Vector3()).length() / 2, 0.001);
      state.radius = radius;
      camera.near = radius / 10000; camera.far = radius * 1000;
      camera.position.copy(center).add(new THREE.Vector3(0, 0, radius / Math.sin(Math.PI / 8)));
      camera.updateProjectionMatrix(); controls.target.copy(center); controls.maxDistance = radius * 100;
      raycaster.params.Points.threshold = radius * 0.006;
      root.rotation.x = live.current.pitch || 0;
      state.modelLoaded = true; setModelLoaded(true);
      setCapabilities({ cameras: hasCameras, colors: hasColors, mesh: hasMesh });
      setLoading(false);
    }, event => { if (!cancelled && event.total) setProgress(Math.round(event.loaded / event.total * 100)); }, () => {
      if (!cancelled) { setError('The 3D model could not be loaded. Try downloading the GLB or creating another reconstruction.'); setLoading(false); }
    });

    let down = null;
    const pointers = new Set();
    const pointerDown = event => {
      pointers.add(event.pointerId);
      down = pointers.size > 1 ? null : { x: event.clientX, y: event.clientY, id: event.pointerId, button: event.button };
    };
    const pointerUp = event => {
      pointers.delete(event.pointerId);
      const start = down; down = null;
      if (!start || start.id !== event.pointerId || start.button !== 0 ||
          Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5 || live.current.mode === 'navigate') return;
      const rect = renderer.domElement.getBoundingClientRect();
      raycaster.setFromCamera(new THREE.Vector2((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1), camera);
      const hit = raycaster.intersectObjects(state.pickables, false)[0];
      if (!hit) { setError('Select a visible point on the reconstruction.'); return; }
      const worldPoint = hit.object.isPoints
        ? hit.object.localToWorld(new THREE.Vector3().fromBufferAttribute(hit.object.geometry.attributes.position, hit.index))
        : hit.point.clone();
      const point = root.worldToLocal(worldPoint).toArray();
      const current = live.current;
      if (current.draft.length && distance(point, current.draft[current.draft.length - 1]) < radiusEpsilon(state.radius)) return;
      setError(null);
      if (current.mode !== 'reference' && current.count >= 200) { setError('Remove a measurement before adding another (maximum 200).'); return; }
      const next = [...current.draft, point];
      if (current.mode === 'reference' && next.length === 2) {
        setPendingReference(next); setDraft([]); setMode('navigate');
      } else if (current.mode === 'distance' && next.length === 2) {
        setSaved(previous => previous.measurements.length >= 200 ? previous : {
          ...previous, measurements: [...previous.measurements, { kind: 'distance', points: next }]
        });
        setDraft([]);
      } else if (next.length <= 100) setDraft(next);
    };
    const pointerCancel = event => { pointers.delete(event.pointerId); down = null; };
    renderer.domElement.addEventListener('pointerdown', pointerDown);
    renderer.domElement.addEventListener('pointerup', pointerUp);
    renderer.domElement.addEventListener('pointercancel', pointerCancel);
    return () => {
      state.modelLoaded = false;
      cancelled = true; cancelAnimationFrame(frame); observer.disconnect(); controls.dispose();
      renderer.domElement.removeEventListener('pointerdown', pointerDown);
      renderer.domElement.removeEventListener('pointerup', pointerUp);
      renderer.domElement.removeEventListener('pointercancel', pointerCancel);
      state.records.forEach(record => { record.geometry.dispose(); if (record.filtered) record.filtered.dispose(); });
      dispose(scene); renderer.dispose(); renderer.forceContextLoss(); renderer.domElement.remove();
      engine.current = null;
    };
  }, [url]);

  useEffect(() => {
    const state = engine.current;
    if (!state || loading) return;
    state.pickables = [];
    state.records.forEach(record => {
      const { object, geometry, isCamera } = record;
      if (isCamera) { object.visible = options.cameras; return; }
      if (!object.isMesh && !object.isPoints) return;
      if (record.filtered) record.filtered.dispose();
      const filtered = geometry.clone();
      const colors = geometry.attributes.color;
      const keep = i => {
        if (!colors) return true;
        const r = colors.getX(i), g = colors.getY(i), b = colors.getZ(i);
        return !(options.black && (r + g + b) * 255 < 16) &&
          !(options.white && Math.min(r, g, b) * 255 > 240);
      };
      const accepted = new Uint8Array(geometry.attributes.position.count);
      for (let i = 0; i < accepted.length; i += 1) accepted[i] = keep(i) ? 1 : 0;
      const indices = [];
      if (options.mesh && object.isMesh) {
        const count = geometry.index ? geometry.index.count : geometry.attributes.position.count;
        for (let i = 0; i < count; i += 3) {
          const triangle = [0, 1, 2].map(offset => geometry.index ? geometry.index.getX(i + offset) : i + offset);
          if (triangle.every(i => accepted[i])) indices.push(...triangle);
        }
      } else {
        for (let i = 0; i < geometry.attributes.position.count; i += 1) if (accepted[i]) indices.push(i);
      }
      filtered.setIndex(indices); record.filtered = filtered;
      if (options.mesh && object.isMesh) {
        object.geometry = filtered; object.visible = true;
        if (record.points) record.points.visible = false;
        state.pickables.push(object);
      } else if (object.isPoints) {
        object.geometry = filtered; object.visible = true; state.pickables.push(object);
      } else {
        object.visible = false;
        if (!record.points) {
          record.points = new THREE.Points(filtered, new THREE.PointsMaterial({
            size: state.radius * 0.003, vertexColors: !!colors, color: '#ffffff'
          }));
          record.points.matrixAutoUpdate = false;
          object.parent.add(record.points);
        }
        record.points.matrix.copy(object.matrix); record.points.geometry = filtered; record.points.visible = true;
        state.pickables.push(record.points);
      }
    });
  }, [options, loading]);

  useEffect(() => {
    const state = engine.current;
    if (!state || loading || !labelLayer.current) return;
    dispose(state.overlay); state.overlay.clear(); state.labels = []; labelLayer.current.replaceChildren();
    const items = saved.measurements.map((m, i) => ({ ...m, label: `${i + 1}: ${format(measurementValue(m, scale, saved.unit))} ${saved.unit}${m.kind === 'area' ? '²' : ''}` }));
    if (saved.reference) items.push({ points: saved.reference.points, label: `Reference: ${format(saved.reference.metres / UNITS[saved.unit])} ${saved.unit}`, color: '#dc2626' });
    if (pendingReference) items.push({ points: pendingReference, label: 'New reference', color: '#dc2626' });
    if (draft.length) items.push({ points: draft, kind: mode === 'area' ? 'area' : 'distance', color: '#f59e0b' });
    items.forEach(item => {
      const color = item.color || '#2563eb';
      const points = item.points.map(p => new THREE.Vector3(...p));
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true });
      const line = item.kind === 'area' && points.length > 2 ? new THREE.LineLoop(geometry, material) : new THREE.Line(geometry, material);
      line.renderOrder = 10; state.overlay.add(line);
      if (item.kind === 'area' && polygonInfo(item.points).valid) {
        const normal = polygonInfo(item.points).normal;
        const drop = normal.map(Math.abs).indexOf(Math.max(...normal.map(Math.abs)));
        const contour = item.points.map(p => new THREE.Vector2(...p.filter((_, i) => i !== drop)));
        const fillGeometry = new THREE.BufferGeometry().setFromPoints(points);
        fillGeometry.setIndex(THREE.ShapeUtils.triangulateShape(contour, []).flat());
        const fill = new THREE.Mesh(fillGeometry, new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, transparent: true, opacity: 0.15, depthTest: false, depthWrite: false }));
        fill.renderOrder = 9; state.overlay.add(fill);
      }
      points.forEach(point => {
        const marker = new THREE.Mesh(new THREE.SphereGeometry(state.radius * 0.004, 8, 6), new THREE.MeshBasicMaterial({ color, depthTest: false }));
        marker.position.copy(point); marker.renderOrder = 11; state.overlay.add(marker);
      });
      if (item.label) {
        const element = document.createElement('span'); element.textContent = item.label;
        Object.assign(element.style, { position: 'absolute', transform: 'translate(-50%, -130%)', padding: '3px 6px', borderRadius: '4px', background: '#172033', color: 'white', fontSize: '13px', whiteSpace: 'nowrap' });
        labelLayer.current.appendChild(element);
        state.labels.push({ element, position: points.reduce((sum, p) => sum.add(p), new THREE.Vector3()).divideScalar(points.length) });
      }
    });
  }, [saved, draft, pendingReference, mode, loading]);

  useEffect(() => {
    const state = engine.current;
    if (!state || loading) return;
    state.root.rotation.x = pitch * Math.PI / 180;
    state.root.updateMatrixWorld(true);
    const bounds = new THREE.Box3();
    state.records.filter(r => !r.isCamera).forEach(r => bounds.expandByObject(r.object));
    const target = bounds.getCenter(new THREE.Vector3());
    const delta = target.clone().sub(state.controls.target);
    state.camera.position.add(delta); state.controls.target.copy(target);
  }, [pitch, loading]);

  useEffect(() => {
    if (engine.current) {
      engine.current.overlay.visible = measurementOpen;
      engine.current.scene.background.set(measurementOpen ? '#161c25' : '#f5f6f8');
    }
  }, [measurementOpen, loading]);

  const changeMode = next => { setMode(next); setDraft([]); setPendingReference(null); setError(null); };
  const finishArea = () => {
    const info = polygonInfo(draft);
    if (!info.valid) { setError('Select at least three distinct corners in boundary order. Edges must not cross.'); return; }
    if (saved.measurements.length >= 200) { setError('Remove a measurement before adding another (maximum 200).'); return; }
    setSaved(previous => ({ ...previous, measurements: [...previous.measurements, { kind: 'area', points: draft }] }));
    setDraft([]); setMode('navigate');
  };
  const applyReference = () => {
    const value = Number(referenceValue), points = pendingReference || (saved.reference && saved.reference.points);
    if (!points || !Number.isFinite(value) || value <= 0 || distance(...points) <= 0) {
      setError('Select two reference points and enter a distance greater than zero.'); return;
    }
    setSaved(previous => ({ ...previous, reference: { points, metres: value * UNITS[previous.unit] } }));
    setPendingReference(null); setError(null);
  };
  const openMeasurements = () => {
    changeMode(saved.reference ? 'distance' : 'reference');
    setMeasurementOpen(true);
  };
  const closeMeasurements = () => {
    changeMode('navigate');
    setReferenceValue(saved.reference ? String(saved.reference.metres / UNITS[saved.unit]) : '');
    setMeasurementOpen(false);
  };
  const downloadImage = async () => {
    if (loading || !modelLoaded || !engine.current || !engine.current.modelLoaded || downloadingImage) return;
    setDownloadingImage(true); setError(null);
    try {
      const legend = [
        '3D reconstruction measurements',
        saved.reference ? 'Reference scale applied.' : 'Estimated scale. Set a known reference for calibrated measurements.'
      ];
      if (saved.reference) legend.push(`Reference: ${format(saved.reference.metres / UNITS[saved.unit])} ${saved.unit}`);
      saved.measurements.forEach((measurement, index) => legend.push(
        `${index + 1}: ${measurement.kind === 'area' ? 'Planar area' : 'Distance'} — ${format(measurementValue(measurement, scale, saved.unit))} ${saved.unit}${measurement.kind === 'area' ? '²' : ''}`
      ));
      const canvas = reconstructionImage(engine.current, legend);
      const blob = await pngBlob(canvas);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl; link.download = 'cs2n-reconstruction-measurements.png';
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (_) {
      setError('The image could not be downloaded. Try again after the model finishes loading.');
    } finally { setDownloadingImage(false); }
  };
  const instructions = mode === 'reference' ? 'Select two points whose real distance you know.' : mode === 'distance' ? 'Select two points on the model to measure their straight-line distance.' : mode === 'area' ? 'Select corners around a flat surface in boundary order, then choose Finish Area.' : 'Drag to rotate · Scroll or pinch to zoom · Right-drag to pan';

  return <ReconstructionMeasurementWorkspace open={measurementOpen} loading={loading} onOpen={openMeasurements} onClose={closeMeasurements} downloadUrl={url} onCreateAnother={onCreateAnother}>
  <div>
    <div>{measurementOpen && <>
    <div style={row} role="group" aria-label="3D measurement tools">
      {[['navigate', 'Navigate'], ['reference', 'Set Reference'], ['distance', 'Measure Distance'], ['area', 'Measure Area']].map(([value, label]) =>
        <button key={value} type="button" className={button} disabled={loading} aria-pressed={mode === value} style={mode === value ? { background: '#2563eb', color: 'white' } : {}} onClick={() => changeMode(value)}>{label}</button>)}
      <button type="button" className="btn btn-success radius" disabled={loading || !modelLoaded || downloadingImage} onClick={downloadImage}>
        <i className="bx bx-download" aria-hidden="true" /> {downloadingImage ? 'Creating Image…' : 'Download Image'}
      </button>
      <label style={{ margin: 0 }}>Units <select style={{ width: 75, margin: 0 }} value={saved.unit} onChange={event => {
        const unit = event.target.value;
        if (referenceValue !== '' && Number.isFinite(Number(referenceValue))) setReferenceValue(String(Number(referenceValue) * UNITS[saved.unit] / UNITS[unit]));
        setSaved(previous => ({ ...previous, unit }));
      }}>{Object.keys(UNITS).map(unit => <option key={unit}>{unit}</option>)}</select></label>
    </div>
    </>}</div>
    <p aria-live="polite" className="small-text">{instructions} {draft.length > 0 && `${draft.length} point${draft.length === 1 ? '' : 's'} selected.`}</p>
    <div>{measurementOpen && <>
    {mode !== 'navigate' && <div style={row}>
      <button type="button" className={button} disabled={!draft.length} onClick={() => setDraft(previous => previous.slice(0, -1))}>Undo Point</button>
      {mode === 'area' && <button type="button" className="btn radius" disabled={draft.length < 3} onClick={finishArea}>Finish Area</button>}
      <button type="button" className={button} onClick={() => changeMode('navigate')}>Cancel</button>
    </div>}
    {(pendingReference || saved.reference) && <div style={{ ...row, alignItems: 'flex-end' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: 0 }}>Reference distance ({saved.unit}) <input type="number" min="0" step="any" value={referenceValue} onChange={event => setReferenceValue(event.target.value)} style={{ width: 140, height: 44, margin: 0 }} /></label>
      <button type="button" style={{ height: 44, margin: 0 }} className="btn radius" onClick={applyReference}>{pendingReference ? 'Apply Reference' : 'Update Reference'}</button>
      {pendingReference && <button type="button" style={{ height: 44, margin: 0 }} className={button} onClick={() => setPendingReference(null)}>Cancel Reference</button>}
      {saved.reference && <button type="button" style={{ height: 44, margin: 0 }} className={button} onClick={() => { setSaved(previous => ({ ...previous, reference: null })); setReferenceValue(''); }}>Clear Reference</button>}
    </div>}
    </>}</div>
    <div style={{ position: 'relative', height: measurementOpen ? 'clamp(280px, 58vh, 720px)' : 550, border: `1px solid ${measurementOpen ? '#394655' : '#dfe4ea'}`, borderRadius: 10, overflow: 'hidden' }}>
      <div ref={host} style={{ width: '100%', height: '100%', cursor: mode === 'navigate' ? 'grab' : 'crosshair' }} />
      <div ref={labelLayer} style={{ display: measurementOpen ? 'block' : 'none', position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }} />
      {loading && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', background: '#f5f6f8' }} role="status">Loading 3D model… {progress > 0 && `${progress}%`}</div>}
    </div>
    {error && <p className="danger radius pad-1" role="alert">{error}</p>}
    <fieldset style={{ marginTop: 12 }}>
      <legend className="small-text text-bold">Display options (live updates)</legend>
      <div style={row}>{[['cameras', 'Show Camera', !capabilities.cameras], ['mesh', 'Show Mesh', !capabilities.mesh], ['black', 'Filter Black Background', !capabilities.colors], ['white', 'Filter White Background', !capabilities.colors]].map(([key, label, disabled]) =>
        <label key={key} style={{ margin: 0 }}><input type="checkbox" checked={options[key]} disabled={loading || disabled} onChange={event => { const checked = event.target.checked; setOptions(previous => ({ ...previous, [key]: checked })); }} /> {label}</label>)}</div>
      {!loading && !capabilities.colors && <p className="small-text inactive-text">Background filters are unavailable for this model.</p>}
    </fieldset>
    <div>{measurementOpen && <>
    <div style={row}><span className="small-text">Floor looks vertical?</span>
      <button className={button} type="button" onClick={() => setPitch(p => p - 90)}>Tilt -90°</button>
      <button className={button} type="button" onClick={() => setPitch(p => p + 90)}>Tilt +90°</button>
      {pitch !== 0 && <button className={button} type="button" onClick={() => setPitch(0)}>Reset Tilt</button>}
    </div>
    </>}</div>
    <div>{measurementOpen && <>
    <p className="small-text inactive-text">{saved.reference ? 'Reference scale applied.' : 'Estimated scale. Set a reference using a known distance.'} Area assumes a flat surface. Measurements depend on reconstruction accuracy.</p>
    {saved.measurements.length > 0 && <div style={{ overflowX: 'auto' }}><table>
      <thead><tr><th>#</th><th>Measurement</th><th>Value</th><th>Action</th></tr></thead>
      <tbody>{saved.measurements.map((measurement, i) => <tr key={i}>
        <td>{i + 1}</td><td>{measurement.kind === 'area' ? 'Planar area' : 'Distance'}{measurement.kind === 'area' && polygonInfo(measurement.points).nonPlanar && <div className="small-text">Corners are not coplanar; area is approximate.</div>}</td>
        <td>{format(measurementValue(measurement, scale, saved.unit))} {saved.unit}{measurement.kind === 'area' && '²'}</td>
        <td><button type="button" className={button} aria-label={`Remove measurement ${i + 1}`} onClick={() => setSaved(previous => ({ ...previous, measurements: previous.measurements.filter((_, index) => index !== i) }))}>Remove</button></td>
      </tr>)}</tbody>
    </table></div>}
    <p className="small-text inactive-text">{storageError || 'Reference and measurements are saved in this browser.'}</p>
    </>}</div>
  </div>
  </ReconstructionMeasurementWorkspace>;
}

const radiusEpsilon = radius => radius * 1e-8;
